import { useState } from "react";
import { cn } from "@/lib/utils";
import {
  bestOutcome,
  formatDayLong,
  formatPrice,
  formatRaceTime,
  topTipWon,
} from "@/utils/race-results";
import { Cloth, DayStatus, EditionPills, EmptyState } from "./parts";

const DOT = {
  top: "border-brand-pink bg-brand-pink text-white",
  tip: "border-brand-pink bg-[#FDF2F8] text-[#C8007E]",
  miss: "border-gray-200 bg-white text-gray-500",
};
const DOT_TITLE = {
  top: "a Lexi Top Tip won",
  tip: "the winner was in Lexi's tips 2–4",
  miss: "Lexi's tips didn't win",
};

function RaceDot({ race }) {
  const kind = bestOutcome(race);
  return (
    <span
      className={cn(
        "grid h-[30px] w-[30px] place-items-center rounded-full border-2 text-xs font-extrabold",
        DOT[kind],
      )}
      title={`R${race.race}: ${DOT_TITLE[kind]}`}
    >
      {race.race}
    </span>
  );
}

/** One card per meeting with a dot per race; tap a meeting for its races. */
export default function MeetingsView({ days }) {
  const [open, setOpen] = useState(() => new Set());
  const visible = days.filter((d) => d.status !== "success" || d.races.length);
  if (!visible.length) return <EmptyState>No meetings match.</EmptyState>;

  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap gap-x-4 gap-y-2 text-xs text-gray-500">
        {["top", "tip", "miss"].map((kind, i) => (
          <span key={kind} className="inline-flex items-center gap-1.5">
            <span
              className={cn(
                "grid h-6 w-6 place-items-center rounded-full border-2 text-[11px] font-extrabold",
                DOT[kind],
              )}
            >
              {i + 1}
            </span>
            {kind === "top"
              ? "A Lexi Top Tip won"
              : kind === "tip"
                ? "Winner was in tips 2–4"
                : "Didn't win"}
          </span>
        ))}
      </div>

      {visible.map((day) => {
        const label = formatDayLong(day.iso);
        if (day.status !== "success")
          return <DayStatus key={day.iso} day={day} label={label} />;

        const tracks = [...new Set(day.races.map((r) => r.track))].sort();
        return (
          <section key={day.iso} className="grid gap-3">
            <div className="flex flex-wrap items-baseline gap-x-2.5">
              <h3 className="text-[17px] font-extrabold tracking-tight">
                {label}
              </h3>
              <span className="text-[13px] tabular-nums text-gray-500">
                {tracks.length} meetings · a Top Tip won{" "}
                {day.races.filter(topTipWon).length} of {day.races.length} races
              </span>
            </div>
            <div className="grid items-start gap-3 md:grid-cols-2">
              {tracks.map((track) => {
                const races = day.races
                  .filter((r) => r.track === track)
                  .sort((a, b) => a.race - b.race);
                const id = `${day.iso}|${track}`;
                const isOpen = open.has(id);
                return (
                  <div
                    key={id}
                    className="min-w-0 overflow-hidden rounded-xl border border-gray-100 bg-white shadow-lg shadow-black/[0.04]"
                  >
                    <button
                      type="button"
                      aria-expanded={isOpen}
                      onClick={() =>
                        setOpen((s) => {
                          const next = new Set(s);
                          if (next.has(id)) next.delete(id);
                          else next.add(id);
                          return next;
                        })
                      }
                      className="grid w-full gap-2.5 px-4 py-3.5 text-left"
                    >
                      <span className="flex items-baseline justify-between gap-2.5">
                        <span className="min-w-0">
                          <b className="text-base font-extrabold">{track}</b>
                          {races[0]?.state && (
                            <small className="ml-1.5 text-xs font-semibold text-gray-500">
                              {races[0].state}
                            </small>
                          )}
                        </span>
                        <span className="whitespace-nowrap text-[13px] tabular-nums text-gray-600">
                          <b className="text-[15px] text-[#C8007E]">
                            {races.filter(topTipWon).length}
                          </b>{" "}
                          of {races.length} won
                        </span>
                      </span>
                      <span className="flex flex-wrap gap-1.5">
                        {races.map((race) => (
                          <RaceDot key={race.id} race={race} />
                        ))}
                      </span>
                    </button>
                    {isOpen && (
                      <div className="border-t border-gray-100">
                        {races.map((race) => (
                          <div
                            key={race.id}
                            className="grid grid-cols-[26px_minmax(0,1fr)] items-center gap-x-2.5 gap-y-1.5 border-t border-gray-100 px-4 py-2 text-[13.5px] first:border-t-0 sm:grid-cols-[34px_minmax(0,1fr)_auto]"
                          >
                            <span className="font-extrabold text-gray-500">
                              R{race.race}
                            </span>
                            <span className="flex min-w-0 items-center gap-1.5 font-bold">
                              <Cloth no={race.winner.no} />
                              <span className="truncate">
                                {race.winner.name}{" "}
                                <small className="font-medium tabular-nums text-gray-500">
                                  {formatPrice(race.winner.sp)} ·{" "}
                                  {formatRaceTime(race.start)}
                                </small>
                              </span>
                            </span>
                            <EditionPills
                              race={race}
                              className="col-start-2 sm:col-start-auto sm:justify-end"
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
}
